import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * FAQ — accordion UI that doubles as FAQPage structured data.
 * Google extracts the Q&A directly, which can produce expandable
 * FAQ snippets in search results.
 *
 * Props:
 *  - items: [{ q: "question", a: "plain-text answer" }, ...]
 *  - heading: optional heading above the list
 *  - eyebrow: optional pre-heading label
 */
export default function FAQ({ items = [], heading = "Frequently asked questions", eyebrow }) {
  const [open, setOpen] = useState(0);

  return (
    <section className="container-x py-20" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        {eyebrow && <div className="eyebrow text-orange mb-3">{eyebrow}</div>}
        <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">{heading}</h2>

        <div className="mt-10 divide-y divide-[rgba(10,14,26,0.08)]">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} data-testid={`faq-item-${i}`} className="py-5">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-start gap-4 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-display font-semibold text-navy text-base md:text-lg flex-1">{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 mt-1 text-[#FF6B35] transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr] mt-3" : "grid-rows-[0fr]"}`}
                >
                  <div className="overflow-hidden">
                    <p className="ink text-sm md:text-base leading-relaxed max-w-2xl">{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Build the FAQPage JSON-LD schema for the given Q&A items.
 * Pass the return value into <SEO schema={faqSchema(items)} />.
 */
export function faqSchema(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };
}
